import { z } from 'zod'
import navTargetsJson from '@/ai/nav-targets.json'
import { STAFF_ROLES, canAccessAdminRoute } from '@/lib/staff-roles'

// ── Bhookly AI navigation registry ──────────────────────────────────────────
// `src/ai/nav-targets.json` is the one list of screens the copilot may open.
// The AI platform builds its per-page tools from the same file, and the widget
// checks every `ui_action` the copilot sends against it here before acting:
// a target the registry does not know is refused, never guessed.
//
// Plan: docs/BHOOKLY_AI_COPILOT_V2_PLAN_2026-10-13.md, Phase 4 + Appendix D/E.

/** Every flag a target may require. Pinned to aiNavFlagVocabulary in backend/internal/handlers/ai_flags.go. */
export const AI_NAV_FLAGS = ['pos', 'kitchen_flow', 'online_ordering', 'house_accounts', 'rider_app', 'receipt_scan', 'hr_payroll'] as const
export type AINavFlag = (typeof AI_NAV_FLAGS)[number]

export const NAV_TARGET_KINDS = ['route', 'tab', 'dialog', 'drawer'] as const

const navParamSchema = z.object({
  /** The URL search key the value goes into (e.g. `item`). */
  url_key: z.string().min(1),
  type: z.enum(['uuid', 'date', 'enum', 'slug']),
  values: z.array(z.string()).optional(),
  required: z.boolean().optional(),
})

const navGroupSchema = z.object({
  tool: z.string().min(1),
  view_arg: z.string().min(1),
  /** Target id the tool opens when no view is named; null when the tool has no home. */
  home: z.string().nullable(),
  description: z.string(),
})

const navTargetSchema = z.object({
  id: z.string().min(1),
  tool: z.string().min(1),
  key: z.string().min(1),
  kind: z.enum(NAV_TARGET_KINDS),
  label: z.string().min(1),
  aliases: z.array(z.string()),
  roman_urdu_aliases: z.array(z.string()),
  route: z.string().startsWith('/'),
  /** Fixed search params the target always opens with. */
  search: z.record(z.string(), z.string()),
  params: z.array(navParamSchema),
  click_target: z.string().nullable(),
  missing_hint: z.string().optional(),
  timeout_ms: z.number().int().positive().optional(),
  roles: z.array(z.enum(STAFF_ROLES)).min(1),
  flags: z.array(z.enum(AI_NAV_FLAGS)),
})

export const navRegistrySchema = z.object({
  version: z.number().int().positive(),
  groups: z.array(navGroupSchema),
  targets: z.array(navTargetSchema),
})

export type NavRegistry = z.infer<typeof navRegistrySchema>
export type NavGroup = z.infer<typeof navGroupSchema>
export type NavTarget = z.infer<typeof navTargetSchema>

/** The raw JSON, for contract tests. */
export const NAV_TARGETS_RAW: unknown = navTargetsJson

const parsed = navRegistrySchema.safeParse(navTargetsJson)
if (!parsed.success) {
  // Never throw at import: this module loads on every /admin screen, and a bad
  // registry must cost the copilot its navigation, not the till its UI. The
  // contract test fails CI on the same parse.
  console.error('[bhookly-ai] nav-targets.json failed validation; navigation disabled', parsed.error.issues)
}

/** The parsed registry; empty (every action refused) if the file is malformed. */
export const navRegistry: NavRegistry = parsed.success ? parsed.data : { version: 1, groups: [], targets: [] }

const targetsById: ReadonlyMap<string, NavTarget> = new Map(navRegistry.targets.map((t) => [t.id, t]))

export function getNavTarget(id: string): NavTarget | undefined {
  return targetsById.get(id)
}

export interface UiActionContext {
  /** The signed-in staff member's role. */
  role: string
  /** ai_nav_flags from GET /settings. */
  flags: readonly string[]
  /** Full paths the router actually registers. */
  knownRoutes: ReadonlySet<string>
}

/**
 * Why an action was refused. `role` is the only one the staff member can do
 * something about (ask the owner), so the widget words it differently.
 */
export type UiActionRefusal =
  | 'invalid_action'
  | 'unknown_target'
  | 'role'
  | 'flag'
  | 'route'
  | 'search'
  | 'click_target'

export type UiActionValidation = { ok: true; target: NavTarget } | { ok: false; reason: UiActionRefusal }

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function searchMatches(actual: unknown, expected: Record<string, string>): boolean {
  // An absent search is the same as an empty one; anything else must be a
  // plain object of exactly the target's keys and values.
  const a = actual === undefined || actual === null ? {} : actual
  if (!isPlainRecord(a)) return false
  const aKeys = Object.keys(a)
  const eKeys = Object.keys(expected)
  if (aKeys.length !== eKeys.length) return false
  return eKeys.every((k) => Object.prototype.hasOwnProperty.call(a, k) && a[k] === expected[k])
}

/**
 * Checks a copilot `ui_action` against the registry before the widget acts on
 * it. In order: the target id exists → the role is listed on the target and
 * passes the app's own route guard → every flag the target needs is on → the
 * route is the target's and the router has it → the search is exactly the
 * target's fixed search (no target takes params yet) → no click target (the
 * click executor comes later). The action's own `label` is display text only
 * and is never trusted for anything.
 */
export function validateUiAction(action: unknown, ctx: UiActionContext): UiActionValidation {
  if (!isPlainRecord(action) || action.type !== 'navigate' || typeof action.target !== 'string') {
    return { ok: false, reason: 'invalid_action' }
  }
  const target = targetsById.get(action.target)
  if (!target) return { ok: false, reason: 'unknown_target' }

  if (!(target.roles as readonly string[]).includes(ctx.role) || !canAccessAdminRoute(ctx.role, target.route)) {
    return { ok: false, reason: 'role' }
  }
  if (!target.flags.every((f) => ctx.flags.includes(f))) {
    return { ok: false, reason: 'flag' }
  }
  if (action.route !== target.route || !ctx.knownRoutes.has(target.route)) {
    return { ok: false, reason: 'route' }
  }
  if (!searchMatches(action.search, target.search)) {
    return { ok: false, reason: 'search' }
  }
  if ((action.click_target ?? null) !== null || target.click_target !== null) {
    return { ok: false, reason: 'click_target' }
  }
  return { ok: true, target }
}
